import { useCallback, useEffect, useState } from 'react';
import type { HmdDevice, Network, PCInstance, Room } from '../../types/database';
import type { PCAgentClient, HmdAssignmentRow } from '../../services/pcAgent';
import { databaseService } from '../../services/database';
import DeviceLabelEditor from './DeviceLabelEditor';
import Toast, { ToastData } from '../Toast';

/**
 * Software → Headsets: per room, the headsets ASSIGNED to that room's PCs plus the
 * free (unassigned) headsets that announced on the room's network. A headset
 * assigned to a PC in another room is not listed here; "Headset toevoegen" pulls
 * a free headset, or one from another room (with a confirmation), into this room.
 *
 * The desired PC lives in infra.hmd_devices.paired_pc_instance_id (operator-owned).
 * The room PRIMARY's agent reports each connected headset's actual BStreaming.json
 * (in sync / drift) but never rewrites it by itself — Push does, and Push restarts
 * that headset's streaming, so it is a between-sessions action.
 */
interface HeadsetsPanelProps {
  rooms: Room[];
  pcsByRoom: Map<string, PCInstance[]>;
  clientFor: (pc: PCInstance) => PCAgentClient;
}

const timeAgo = (iso?: string | null): string => {
  if (!iso) return '–';
  const secs = Math.max(0, (Date.now() - new Date(iso).getTime()) / 1000);
  if (secs < 20) return 'nu';
  if (secs < 90) return `${Math.round(secs)}s geleden`;
  const mins = secs / 60;
  if (mins < 60) return `${Math.round(mins)}m geleden`;
  const hrs = mins / 60;
  if (hrs < 24) return `${Math.round(hrs)}u geleden`;
  return `${Math.round(hrs / 24)}d geleden`;
};

// Outcome → what the operator needs to know, in one word + a colour.
const OUTCOME_LABEL: Record<string, { text: string; cls: string }> = {
  in_sync: { text: 'Correct', cls: 'ok' },
  drift: { text: 'Wijkt af — Push nodig', cls: 'warn' },
  written: { text: 'Gepusht', cls: 'ok' },
  unassigned: { text: 'Geen PC gekozen', cls: 'muted' },
  elsewhere: { text: 'Nog in andere ruimte', cls: 'warn' },
  unchecked: { text: 'Nog niet gecontroleerd', cls: 'muted' },
  offline: { text: 'Headset offline', cls: 'muted' },
  other_network: { text: 'PC in ander netwerk', cls: 'warn' },
  pc_no_ip: { text: 'PC heeft geen IP', cls: 'warn' },
  no_all_files_access: { text: 'Geen bestandsrechten op headset', cls: 'err' },
  read_failed: { text: 'Uitlezen mislukt', cls: 'err' },
  write_failed: { text: 'Schrijven mislukt', cls: 'err' },
};

// A PICO headset has two MAC addresses. The agent announces the DEVICE address
// (e.g. 04:34:C3:89:B6:A0, with the serial); a WiFi 7 router only ever sees the
// per-link STA address (06:34:C3:89:B6:22) — the same MAC with the
// locally-administered bit set and a different last byte, one per radio link.
// The poller therefore creates a second, serial-less hmd_devices row per link,
// and a database trigger copies the pairing onto it so the WiFi telemetry joins
// (which match on the exact router-seen MAC) resolve to a paired headset. Those
// rows are load-bearing, NOT junk — but they must not be shown as if they were
// headsets you can assign or push. So: fold them into their real headset, and
// keep only the ones with no headset behind them, which mean something else
// entirely (a PICO on this WiFi that has never run the shell).
//
// mac_identity mirrors telemetry.mac_identity() in SQL: first five octets with
// the 0x02 bit cleared, equal for both forms.
function macIdentity(mac: string | null | undefined): string | null {
  if (!mac) return null;
  const parts = mac.toUpperCase().split(':');
  if (parts.length < 6) return null;
  const first = parseInt(parts[0], 16);
  if (Number.isNaN(first)) return null;
  const cleared = (first & 0xfd).toString(16).toUpperCase().padStart(2, '0');
  return [cleared, ...parts.slice(1, 5)].join(':');
}


export default function HeadsetsPanel({ rooms, pcsByRoom, clientFor }: HeadsetsPanelProps) {
  const [devices, setDevices] = useState<HmdDevice[]>([]);
  const [retired, setRetired] = useState<HmdDevice[]>([]); // active=false, only offered in the picker
  const [allPcs, setAllPcs] = useState<PCInstance[]>([]); // fleet-wide, to name PCs in other rooms
  const [networks, setNetworks] = useState<Network[]>([]); // fleet-wide, network -> room
  // Per room: the "Headset toevoegen" picker state.
  const [adder, setAdder] = useState<{ roomId: string; deviceId: string; pcId: string } | null>(null);
  const [live, setLive] = useState<Map<string, HmdAssignmentRow>>(new Map()); // by serial
  const [liveByRoom, setLiveByRoom] = useState<Map<string, HmdAssignmentRow[]>>(new Map()); // what each room's primary sees
  const [primaryDown, setPrimaryDown] = useState<Set<string>>(new Set()); // room ids
  const [busy, setBusy] = useState<Set<string>>(new Set());
  const [confirmRetire, setConfirmRetire] = useState<string | null>(null);
  const [toast, setToast] = useState<ToastData | null>(null);
  const [loading, setLoading] = useState(true);

  // Every PC holds the socket of its own headset (the headset dials the PC in
  // its BStreaming.json); the primary holds the strays. So ask every PC of the
  // room and remember which one actually holds each headset.
  const primariesOf = (roomId: string): PCInstance[] => pcsByRoom.get(roomId) || [];
  const [primaryBySerial, setPrimaryBySerial] = useState<Map<string, PCInstance>>(new Map());

  const load = useCallback(async () => {
    setLoading(true);
    const [allRows, pcs, nets] = await Promise.all([
      databaseService.getAllHmdDevices(true),
      databaseService.getAllPCInstances(),
      databaseService.listNetworks(),
    ]);
    const rows = allRows.filter((d) => d.active !== false);
    setDevices(rows);
    setRetired(allRows.filter((d) => d.active === false));
    setAllPcs(pcs);
    setNetworks(nets);
    const liveMap = new Map<string, HmdAssignmentRow>();
    const byRoom = new Map<string, HmdAssignmentRow[]>();
    const holder = new Map<string, PCInstance>();
    const down = new Set<string>();
    await Promise.all(
      rooms.map(async (room) => {
        const primaries = primariesOf(room.id);
        const answers = await Promise.all(
          primaries.map(async (pc) => ({ pc, data: await clientFor(pc).getHmdAssignments() }))
        );
        const live = answers.filter((a) => a.data);
        if (live.length === 0) {
          down.add(room.id);
          return;
        }
        const merged: HmdAssignmentRow[] = [];
        live.forEach(({ pc, data }) => {
          data!.headsets.forEach((h) => {
            const key = h.serial.toUpperCase();
            liveMap.set(key, h);
            holder.set(key, pc);
            merged.push(h);
          });
        });
        byRoom.set(room.id, merged);
      })
    );
    setLive(liveMap);
    setLiveByRoom(byRoom);
    setPrimaryBySerial(holder);
    setPrimaryDown(down);
    setLoading(false);
  }, [rooms, pcsByRoom]); // eslint-disable-line react-hooks/exhaustive-deps

  // The agent to send a push to: the one that holds the headset's socket, else
  // any primary of the room that is reachable.
  const agentForSerial = (roomId: string, serial: string): PCInstance | undefined =>
    primaryBySerial.get(serial.toUpperCase()) || primariesOf(roomId)[0];

  useEffect(() => {
    load();
    const t = setInterval(load, 15000);
    return () => clearInterval(t);
  }, [load]);

  const withBusy = async (key: string, fn: () => Promise<void>) => {
    setBusy((b) => new Set(b).add(key));
    try {
      await fn();
    } finally {
      setBusy((b) => {
        const n = new Set(b);
        n.delete(key);
        return n;
      });
    }
  };

  const handleAssign = (dev: HmdDevice, pcId: string | null) =>
    withBusy(dev.id, async () => {
      const ok = await databaseService.setHmdAssignedPC(dev.id, pcId);
      if (!ok) {
        setToast({ kind: 'error', message: 'Toewijzing opslaan mislukt' });
        return;
      }
      setDevices((ds) => ds.map((d) => (d.id === dev.id ? { ...d, paired_pc_instance_id: pcId } : d)));
      setToast({ kind: 'success', message: pcId ? 'PC toegewezen — druk op Push om de headset om te zetten' : 'Toewijzing verwijderd' });
    });

  const handleLabel = (dev: HmdDevice, label: string) =>
    withBusy(dev.id, async () => {
      const ok = await databaseService.setHmdLabel(dev.id, label);
      if (!ok) {
        setToast({ kind: 'error', message: 'Naam opslaan mislukt' });
        return;
      }
      setDevices((ds) => ds.map((d) => (d.id === dev.id ? { ...d, label: label.trim() || null } : d)));
    });

  const handlePush = (roomId: string, serial: string) =>
    withBusy(`push:${serial}`, async () => {
      const primary = agentForSerial(roomId, serial);
      if (!primary) return;
      const res = await clientFor(primary).hmdPush(serial);
      if (res.success) {
        setToast({ kind: 'success', message: `Headset ${serial.slice(-5)} omgezet naar ${res.data?.assigned_pc_name || 'de gekozen PC'}` });
      } else {
        setToast({ kind: 'error', message: `Push mislukt: ${res.error || 'onbekende fout'}` });
      }
      await load();
    });

  const handleProvision = (roomId: string, serial: string) =>
    withBusy(`prov:${serial}`, async () => {
      const holder = agentForSerial(roomId, serial);
      if (!holder) return;
      const res = await clientFor(holder).hmdProvision(serial);
      if (res.success) {
        setToast({ kind: 'success', message: 'Headset herstart; rechten worden na de herstart automatisch gezet' });
      } else {
        setToast({ kind: 'error', message: `Rechten geven kan nu niet: ${res.error || 'onbekende fout'}` });
      }
      await load();
    });

  const handlePushAll = (roomId: string) =>
    withBusy(`pushall:${roomId}`, async () => {
      // Every reachable primary pushes what it holds; results are summed.
      const targets = new Set<PCInstance>();
      (liveByRoom.get(roomId) || []).forEach((h) => {
        const pc = primaryBySerial.get(h.serial.toUpperCase());
        if (pc) targets.add(pc);
      });
      if (targets.size === 0) primariesOf(roomId).slice(0, 1).forEach((pc) => targets.add(pc));
      let written = 0;
      let failed: string | null = null;
      for (const pc of targets) {
        const res = await clientFor(pc).hmdPushAll();
        if (res.success) written += Object.values(res.data || {}).filter((r) => r.outcome === 'written').length;
        else failed = res.error || 'onbekende fout';
      }
      setToast(failed ? { kind: 'error', message: `Push mislukt: ${failed}` } : { kind: 'success', message: `${written} headset(s) omgezet` });
      await load();
    });

  const handleRetire = (dev: HmdDevice) =>
    withBusy(dev.id, async () => {
      setConfirmRetire(null);
      const ok = await databaseService.retireHmdDevice(dev.id);
      if (!ok) {
        setToast({ kind: 'error', message: 'Verwijderen mislukt' });
        return;
      }
      setDevices((ds) => ds.filter((d) => d.id !== dev.id));
    });

  const pcById = (pcId?: string | null): PCInstance | undefined =>
    pcId ? allPcs.find((p) => p.id === pcId) : undefined;
  const pcName = (_roomId: string, pcId?: string | null) =>
    pcId ? pcById(pcId)?.pc_name || pcId.slice(0, 8) : null;
  const roomIdOfNetwork = (networkId?: string | null): string | undefined =>
    networkId ? networks.find((n) => n.id === networkId)?.room_id : undefined;
  const roomNameById = (roomId?: string): string =>
    roomId ? rooms.find((r) => r.id === roomId)?.name || 'andere ruimte' : 'onbekend netwerk';
  // Where a headset currently belongs: the room of its assigned PC.
  const assignedRoomOf = (dev: HmdDevice): string | undefined =>
    roomIdOfNetwork(pcById(dev.paired_pc_instance_id)?.network_id);

  const handleAdd = (roomId: string) =>
    withBusy(`add:${roomId}`, async () => {
      if (!adder || adder.roomId !== roomId || !adder.deviceId || !adder.pcId) return;
      const dev = devices.find((d) => d.id === adder.deviceId) || retired.find((d) => d.id === adder.deviceId);
      if (!dev) return;
      setAdder(null);
      await handleAssign(dev, adder.pcId);
      await load(); // a retired headset is active again after assignment
    });

  return (
    <div className="software-headsets">
      {toast && <Toast message={toast.message} kind={toast.kind} onClose={() => setToast(null)} />}
      <p className="software-hint">
        Kies per headset de PC waarnaar hij moet streamen. De agent die de headset vasthoudt leest continu
        uit wat de headset nu heeft; <b>Push</b> schrijft de keuze naar de headset en herstart daar de
        streaming — doe dat tussen sessies door.
      </p>
      {loading && devices.length === 0 && <div className="software-hint">Laden…</div>}

      {rooms.map((room) => {
        const roomPcs = pcsByRoom.get(room.id) || [];
        const networkIds = new Set(roomPcs.map((p) => p.network_id));
        const roomPcIds = new Set(roomPcs.map((p) => p.id));
        // Assigned to one of this room's PCs, or free and last seen on this room's network.
        const roomDevices = devices.filter((d) =>
          d.paired_pc_instance_id
            ? roomPcIds.has(d.paired_pc_instance_id)
            : !!d.network_id && networkIds.has(d.network_id)
        );
        // Fold the router's per-link rows into the headset they belong to (see
        // macIdentity): a link row with a device-form sibling adds nothing
        // actionable, its pairing is already synced by the trigger, and its
        // Push button can never do anything. A link row with NO sibling stays
        // visible, because it means a PICO is on this WiFi that no agent has
        // ever seen — i.e. it still needs the shell.
        const identityOfReal = new Map<string, HmdDevice>();
        for (const d of devices) {
          const id = d.serial ? macIdentity(d.wifi_mac) : null;
          if (id) identityOfReal.set(id, d);
        }
        const realDevices = roomDevices.filter((d) => !!d.serial);
        const linkRows = roomDevices.filter((d) => !d.serial);
        const linksByRealId = new Map<string, string[]>();
        const orphanLinks: HmdDevice[] = [];
        for (const l of linkRows) {
          const sib = identityOfReal.get(macIdentity(l.wifi_mac) || '');
          if (sib) {
            const list = linksByRealId.get(sib.id) || [];
            list.push(l.wifi_mac);
            linksByRealId.set(sib.id, list);
          } else {
            orphanLinks.push(l);
          }
        }
        // Candidates for "Headset toevoegen": everything not already in this room.
        const inRoom = new Set(roomDevices.map((d) => d.id));
        const candidates = [...devices.filter((d) => !inRoom.has(d.id) && d.serial), ...retired.filter((d) => d.serial)];
        const adding = adder?.roomId === room.id ? adder : null;
        const addDev = adding ? devices.find((d) => d.id === adding.deviceId) || retired.find((d) => d.id === adding.deviceId) : undefined;
        const addFromRoom = addDev?.paired_pc_instance_id ? assignedRoomOf(addDev) : undefined;
        // Headsets this room's primary sees but the DB does not know yet (announce not landed) — show them too.
        const known = new Set(roomDevices.map((d) => (d.serial || '').toUpperCase()));
        const unregistered = (liveByRoom.get(room.id) || []).filter((h) => !known.has(h.serial.toUpperCase()));
        const primary = primariesOf(room.id)[0];
        const primaryOffline = primaryDown.has(room.id);
        if (realDevices.length === 0 && orphanLinks.length === 0 && unregistered.length === 0 && !adding) return null;

        return (
          <section key={room.id} className="software-room-section">
            <div className="software-room-header">
              <h3>{room.name}</h3>
              <div className="software-actions">
                <button
                  className="software-btn"
                  disabled={busy.has(`add:${room.id}`)}
                  onClick={() => setAdder(adding ? null : { roomId: room.id, deviceId: '', pcId: roomPcs[0]?.id || '' })}
                  title="Een vrije headset, of een headset uit een andere ruimte, aan een PC in deze ruimte toewijzen"
                >
                  Headset toevoegen
                </button>
                {primaryOffline ? (
                  <span className="software-hmd-badge muted">geen agent bereikbaar</span>
                ) : (
                  <button
                    className="software-btn"
                    disabled={!primary || busy.has(`pushall:${room.id}`)}
                    onClick={() => handlePushAll(room.id)}
                    title="Alle afwijkende headsets in deze ruimte omzetten"
                  >
                    Push alles
                  </button>
                )}
              </div>
            </div>

            {adding && (
              <div className="software-hmd-adder">
                <select
                  className="software-select"
                  value={adding.deviceId}
                  onChange={(e) => setAdder({ ...adding, deviceId: e.target.value })}
                >
                  <option value="">Kies een headset…</option>
                  {candidates.map((d) => {
                    const where = d.active === false
                      ? `uit vloot · ${roomNameById(roomIdOfNetwork(d.network_id))}`
                      : d.paired_pc_instance_id
                        ? `${roomNameById(assignedRoomOf(d))} · ${pcName(room.id, d.paired_pc_instance_id)}`
                        : `vrij · ${roomNameById(roomIdOfNetwork(d.network_id))}`;
                    return (
                      <option key={d.id} value={d.id}>
                        {d.label || d.serial} ({where})
                      </option>
                    );
                  })}
                </select>
                <span className="software-hint">naar</span>
                <select
                  className="software-select"
                  value={adding.pcId}
                  onChange={(e) => setAdder({ ...adding, pcId: e.target.value })}
                >
                  {roomPcs.map((pc) => (
                    <option key={pc.id} value={pc.id}>
                      {pc.pc_name || pc.id.slice(0, 8)}{pc.ip_address ? ` (${pc.ip_address})` : ''}
                    </option>
                  ))}
                </select>
                {addDev?.paired_pc_instance_id && (
                  <span className="software-error-inline">
                    Deze headset is nu toegewezen aan {pcName(room.id, addDev.paired_pc_instance_id)} in {roomNameById(addFromRoom)}.
                    Toewijzen haalt hem daar weg.
                  </span>
                )}
                <button
                  className={`software-btn ${addDev?.paired_pc_instance_id ? 'danger' : 'primary'}`}
                  disabled={!adding.deviceId || !adding.pcId || busy.has(`add:${room.id}`)}
                  onClick={() => handleAdd(room.id)}
                >
                  {addDev?.paired_pc_instance_id ? 'Ja, verplaats' : 'Toewijzen'}
                </button>
                <button className="software-btn" onClick={() => setAdder(null)}>Annuleer</button>
                <span className="software-hint">Daarna nog op Push drukken zodra de headset in deze ruimte online is.</span>
              </div>
            )}

            <table className="software-table software-hmd-table">
              <thead>
                <tr>
                  <th>Naam</th>
                  <th>Serienummer</th>
                  <th>Status</th>
                  <th>Toegewezen PC</th>
                  <th>Streamt naar</th>
                  <th>Bestand</th>
                  <th>Agent</th>
                  <th>Rechten</th>
                  <th></th>
                </tr>
              </thead>
              <tbody>
                {realDevices.map((dev) => {
                  const serial = (dev.serial || '').toUpperCase();
                  const l = live.get(serial);
                  const online = !!l;
                  const elsewhere = !!dev.network_id && !networkIds.has(dev.network_id);
                  // Until the holding agent's first drift check (checked_at empty, e.g. right
                  // after an agent restart) the file state is simply unknown.
                  const checked = !!l?.checked_at && !l.checked_at.startsWith('0001');
                  const outcome = l ? (checked ? l.outcome : 'unchecked') : (elsewhere ? 'elsewhere' : dev.paired_pc_instance_id ? 'offline' : 'unassigned');
                  const o = OUTCOME_LABEL[outcome] || { text: outcome, cls: 'muted' };
                  const assignedIp = l?.assigned_ip;
                  const fileIp = l?.bs_ip ?? dev.bs_ip ?? null;
                  const rowBusy = busy.has(dev.id) || busy.has(`push:${serial}`) || busy.has(`prov:${serial}`);
                  const canPush = online && !!dev.paired_pc_instance_id && !primaryOffline && !rowBusy;
                  return (
                    <tr key={dev.id} className={online ? '' : 'software-hmd-offline'}>
                      <td>
                        <DeviceLabelEditor label={dev.label} onSave={(v) => handleLabel(dev, v)} placeholder="bv. 01-CLEOPATRA-01" empty="naamloos" />
                      </td>
                      <td className="software-mono">
                        {serial || <em>onbekend</em>}
                        <div className="software-hint">{dev.wifi_mac}</div>
                        {(linksByRealId.get(dev.id)?.length ?? 0) > 0 && (
                          <div className="software-hint" title={`WiFi 7 link-adressen van deze headset (zo ziet de router hem): ${linksByRealId.get(dev.id)!.join(', ')}`}>
                            +{linksByRealId.get(dev.id)!.length} wifi-link{linksByRealId.get(dev.id)!.length > 1 ? 's' : ''}
                          </div>
                        )}
                      </td>
                      <td>
                        <span className={`software-hmd-badge ${online ? 'ok' : 'muted'}`}>{online ? 'online' : `offline · ${timeAgo(dev.last_announced_at)}`}</span>
                        {elsewhere && !online && (
                          <div className="software-hint">laatst gezien in {roomNameById(roomIdOfNetwork(dev.network_id))}</div>
                        )}
                        {online && l?.battery != null && (
                          <div className="software-hint">{l.battery}%{l.charging ? ' ↑' : ''}{l.worn ? ' · op hoofd' : ''}</div>
                        )}
                      </td>
                      <td>
                        <select
                          className="software-select"
                          value={dev.paired_pc_instance_id || ''}
                          disabled={rowBusy}
                          onChange={(e) => handleAssign(dev, e.target.value || null)}
                        >
                          <option value="">— (vrijgeven)</option>
                          {roomPcs.map((pc) => (
                            <option key={pc.id} value={pc.id}>
                              {pc.pc_name || pc.id.slice(0, 8)}{pc.ip_address ? ` (${pc.ip_address})` : ''}
                            </option>
                          ))}
                        </select>
                      </td>
                      <td>
                        {dev.observed_pc_instance_id ? (
                          <>
                            {pcName(room.id, dev.observed_pc_instance_id)}
                            <div className="software-hint">{timeAgo(dev.observed_at)}</div>
                          </>
                        ) : (
                          <span className="software-hint">nog niet gezien</span>
                        )}
                      </td>
                      <td>
                        <span className={`software-hmd-badge ${o.cls}`}>{o.text}</span>
                        <div className="software-hint software-mono">
                          {fileIp || '—'}{assignedIp && fileIp !== assignedIp ? ` → ${assignedIp}` : ''}
                        </div>
                        {l?.last_error && <div className="software-error-inline">{l.last_error}</div>}
                      </td>
                      <td className="software-hint">{l?.agent_version || dev.agent_version || '–'}{l?.pui ? ` · PUI ${l.pui}` : ''}</td>
                      <td>
                        {(() => {
                          const prov = l?.provision;
                          const needs = outcome === 'no_all_files_access';
                          if (prov && (prov.phase === 'reboot_pending' || prov.phase === 'awaiting_adb')) {
                            return <span className="software-hmd-badge warn">bezig · {prov.phase === 'reboot_pending' ? 'herstart' : 'adb'}</span>;
                          }
                          if (prov?.phase === 'manual') {
                            return (
                              <>
                                <span className="software-hmd-badge err" title={prov.error || ''}>handmatig (kabel)</span>
                                <div><button className="software-btn subtle" disabled={rowBusy || !online} onClick={() => handleProvision(room.id, serial)}>Opnieuw</button></div>
                              </>
                            );
                          }
                          if (needs) {
                            return (
                              <>
                                <span className="software-hmd-badge err">ontbreken</span>
                                <div><button className="software-btn" disabled={rowBusy || !online} onClick={() => handleProvision(room.id, serial)} title="Wireless adb aan, headset herstart, rechten zetten, adb uit. Alleen als de headset niet op een hoofd zit.">Rechten geven</button></div>
                              </>
                            );
                          }
                          return online && checked ? <span className="software-hmd-badge ok">ok</span> : <span className="software-hint">–</span>;
                        })()}
                      </td>
                      <td className="software-actions">
                        <button className="software-btn primary" disabled={!canPush} onClick={() => handlePush(room.id, serial)} title="BStreaming.json schrijven en streaming op de headset herstarten">
                          Push
                        </button>
                        {confirmRetire === dev.id ? (
                          <span className="software-confirm">
                            <button className="software-btn danger" onClick={() => handleRetire(dev)}>Uit vloot halen</button>
                            <button className="software-btn" onClick={() => setConfirmRetire(null)}>Annuleer</button>
                          </span>
                        ) : (
                          <button className="software-btn subtle" disabled={rowBusy} onClick={() => setConfirmRetire(dev.id)} title="Headset uit de vloot halen (komt vanzelf terug zodra hij zich weer aanmeldt). Alleen van een PC afhalen? Kies '— (vrijgeven)'.">
                            Uit vloot
                          </button>
                        )}
                      </td>
                    </tr>
                  );
                })}
                {orphanLinks.map((l) => (
                  <tr key={l.id} className="software-hmd-offline">
                    <td><span className="software-hint">–</span></td>
                    <td className="software-mono">
                      <em>geen agent</em>
                      <div className="software-hint">{l.wifi_mac}</div>
                    </td>
                    <td>
                      <span className="software-hmd-badge muted">alleen op wifi gezien</span>
                      <div className="software-hint">PICO-headset op dit netwerk die nog nooit heeft aangemeld — shell nog niet ge&iuml;nstalleerd</div>
                    </td>
                    <td><span className="software-hint">n.v.t.</span></td>
                    <td><span className="software-hint">–</span></td>
                    <td><span className="software-hint">–</span></td>
                    <td className="software-hint">–</td>
                    <td><span className="software-hint">–</span></td>
                    <td></td>
                  </tr>
                ))}
                {unregistered.map((h) => (
                  <tr key={`live-${h.serial}`}>
                    <td><em>nog niet in database</em></td>
                    <td className="software-mono">{h.serial}</td>
                    <td><span className="software-hmd-badge ok">online</span></td>
                    <td colSpan={6} className="software-hint">Aangemeld bij de agent; de rij verschijnt zodra de announce is opgeslagen.</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </section>
        );
      })}
    </div>
  );
}
